import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import cytoscape, { Core, EventObject, NodeSingular, EdgeSingular, NodeDataDefinition, EdgeDataDefinition } from 'cytoscape';
import '../../core/cytoscape-setup';
import { Sidebar } from '../../shared/components/sidebar/sidebar';
import { Navbar } from '../../shared/components/navbar/navbar';
import { Toast } from '../../shared/services/toast';
import { Toast as ToastComponent } from '../../shared/components/toast/toast';
import { CodeGraph, CodeGraphNode, CodeGraphEdge } from '../../core/services/code-graph';

type LayoutName = 'fcose' | 'breadthfirst' | 'grid' | 'circle' | 'concentric';

const NODE_BATCH_SIZE = 300;
const MAX_LABEL_LENGTH = 22;

function truncateLabel(name: string, maxLength = MAX_LABEL_LENGTH): string {
  if (!name || name.length <= maxLength) return name;
  return `${name.slice(0, maxLength - 1)}…`;
}

@Component({
  selector: 'app-code-graph-viewer',
  standalone: true,
  imports: [CommonModule, Sidebar, Navbar, ToastComponent],
  templateUrl: './code-graph-viewer.html',
  styleUrl: './code-graph-viewer.css'
})
export class CodeGraphViewer implements OnInit, AfterViewInit, OnDestroy {
  private codeGraphService = inject(CodeGraph);
  private toast = inject(Toast);
  private route = inject(ActivatedRoute);
  private router = inject(Router);

  @ViewChild('graphCanvas') graphCanvas?: ElementRef<HTMLDivElement>;

  private cy: Core | null = null;

  // Incremental loading state — nodes stream in NODE_BATCH_SIZE batches so the
  // UI never blocks on a huge one-shot fetch+layout. Edges are fetched once
  // (cheap even at a few thousand rows) and "realized" into the graph as
  // their endpoint nodes arrive across batches.
  private nodeOffset = 0;
  private allEdges: CodeGraphEdge[] = [];
  private loadedNodeIds = new Set<string>();
  private addedEdgeIds = new Set<string>();
  private loadRequestToken = 0;

  repoName = signal<string | null>(null);
  connectionId = signal<string | null>(null);
  scope = signal<'combined' | 'repository'>('combined');
  cameFromClassification = signal(false);

  isLoadingGraph = signal(true);
  isStreamingMore = signal(false);
  nodeCount = signal(0);
  edgeCount = signal(0);

  selectedType = signal<'node' | 'edge' | null>(null);
  selectedTitle = signal('');
  selectedSubtitle = signal('');
  selectedMeta = signal<Array<{ label: string; value: string }>>([]);

  currentLayout = signal<LayoutName>('fcose');
  readonly availableLayouts: LayoutName[] = ['fcose', 'breadthfirst', 'grid', 'circle', 'concentric'];

  private readonly colors = {
    file: { bg: '#DBEAFE', border: '#2563EB' },
    class: { bg: '#EDE9FE', border: '#7C3AED' },
    function: { bg: '#D1FAE5', border: '#059669' },
    other: { bg: '#F1F5F9', border: '#64748B' },
    edge: { color: '#94A3B8', highlight: '#0EA5E9' }
  };

  ngOnInit(): void {
    this.repoName.set(this.route.snapshot.queryParamMap.get('repo'));
    this.connectionId.set(this.route.snapshot.queryParamMap.get('connectionId'));
    this.cameFromClassification.set(history.state?.from === 'classification');
    this.loadGraph();
  }

  ngAfterViewInit(): void {
    this.initGraph();
  }

  ngOnDestroy(): void {
    this.cy?.destroy();
    this.cy = null;
  }

  goBack() {
    if (this.cameFromClassification()) {
      this.router.navigate(['/documents/classified'], { state: { activeTab: 'repos' } });
    } else {
      this.router.navigate(['/data-sources/repositories']);
    }
  }

  loadGraph() {
    const token = ++this.loadRequestToken;

    this.isLoadingGraph.set(true);
    this.isStreamingMore.set(false);
    this.nodeOffset = 0;
    this.allEdges = [];
    this.loadedNodeIds.clear();
    this.addedEdgeIds.clear();
    this.nodeCount.set(0);
    this.edgeCount.set(0);
    this.clearSelection();
    this.cy?.elements().remove();

    this.fetchNextBatch(token, true);
  }

  private fetchNextBatch(token: number, isFirstBatch: boolean) {
    const repo = this.repoName() || undefined;
    const connectionId = this.connectionId();
    if (!connectionId) {
      this.isLoadingGraph.set(false);
      this.toast.show('No GitHub connection selected.', 'error');
      return;
    }

    this.codeGraphService.getGraph(connectionId, repo, this.nodeOffset, NODE_BATCH_SIZE).subscribe({
      next: (res) => {
        if (token !== this.loadRequestToken) return; // a newer loadGraph() superseded this one

        if (isFirstBatch) {
          this.isLoadingGraph.set(false);
          this.scope.set(res.scope);
          this.allEdges = res.edges || [];
        }

        const batchNodes = res.nodes || [];
        this.nodeOffset += batchNodes.length;
        this.appendBatch(batchNodes);

        const hasMore = !!res.meta?.hasMoreNodes;
        this.isStreamingMore.set(hasMore);
        if (hasMore) {
          this.fetchNextBatch(token, false);
        }
      },
      error: (err) => {
        if (token !== this.loadRequestToken) return;
        this.isStreamingMore.set(false);
        if (isFirstBatch) {
          this.isLoadingGraph.set(false);
          const message = err?.error?.message || 'Failed to load code graph.';
          this.toast.show(message, 'error');
        } else {
          this.toast.show('Stopped loading more of the graph after a network error.', 'error');
        }
      }
    });
  }

  private appendBatch(nodes: CodeGraphNode[]) {
    if (!this.cy) {
      this.initGraph();
      if (!this.cy) {
        setTimeout(() => this.appendBatch(nodes), 50);
        return;
      }
    }

    const newElements: cytoscape.ElementDefinition[] = [];

    for (const node of nodes) {
      if (this.loadedNodeIds.has(node.id)) continue;
      this.loadedNodeIds.add(node.id);
      newElements.push({
        data: {
          id: node.id,
          name: truncateLabel(node.name),
          fullName: node.name,
          type: node.label,
          repoName: node.repoName
        }
      });
    }

    for (const edge of this.allEdges) {
      const edgeKey = edge.id || `${edge.source}-${edge.target}`;
      if (this.addedEdgeIds.has(edgeKey)) continue;
      if (!this.loadedNodeIds.has(edge.source) || !this.loadedNodeIds.has(edge.target)) continue;

      this.addedEdgeIds.add(edgeKey);
      newElements.push({
        data: {
          id: edgeKey,
          source: edge.source,
          target: edge.target,
          type: edge.type,
          relationship: edge.relationship || edge.type
        }
      });
    }

    this.nodeCount.set(this.loadedNodeIds.size);
    this.edgeCount.set(this.addedEdgeIds.size);

    if (newElements.length === 0) return;

    const added = this.cy!.add(newElements);
    const isFirstBatch = this.loadedNodeIds.size === added.nodes().length;

    // fcose is fast enough to relayout the WHOLE graph on every batch rather
    // than needing the subset-layout workarounds cose required —
    // randomize:false keeps already-positioned nodes anchored and only
    // computes positions for the newly-added ones, so this stays cheap and
    // visually coherent even as the graph grows to thousands of nodes.
    requestAnimationFrame(() => {
      this.runFcoseLayout(!isFirstBatch);
      if (isFirstBatch) {
        this.cy!.fit(undefined, 50);
      }
    });
  }

  private runFcoseLayout(incremental: boolean) {
    if (!this.cy) return;
    this.cy.layout({
      name: 'fcose',
      quality: 'default',
      animate: false,
      fit: false,
      randomize: !incremental,
      nodeRepulsion: 4500,
      idealEdgeLength: 80,
      edgeElasticity: 0.45,
      numIter: 2500
    } as unknown as cytoscape.LayoutOptions).run();
  }

  resetView() {
    this.cy?.fit(undefined, 50);
  }

  applyLayout(layout: LayoutName) {
    this.currentLayout.set(layout);
    if (!this.cy) return;

    this.cy.resize();

    if (layout === 'fcose') {
      // User-triggered relayout: always a fresh randomize, not incremental.
      this.runFcoseLayout(false);
      this.cy.fit(undefined, 50);
      return;
    }

    const layoutOptions: Record<string, unknown> = {
      name: layout,
      animate: true,
      animationDuration: 500,
      fit: true,
      padding: 40
    };

    if (layout === 'breadthfirst') {
      layoutOptions['directed'] = true;
      layoutOptions['spacingFactor'] = 2.0;
    }

    if (layout === 'grid') {
      layoutOptions['spacingFactor'] = 2.0;
    }

    if (layout === 'circle' || layout === 'concentric') {
      layoutOptions['spacingFactor'] = 1.8;
    }

    this.cy.layout(layoutOptions as unknown as cytoscape.LayoutOptions).run();
  }

  clearSelection() {
    this.selectedType.set(null);
    this.selectedTitle.set('');
    this.selectedSubtitle.set('');
    this.selectedMeta.set([]);
  }

  private nodeSize(ele: NodeSingular): number {
    const name = (ele.data('name') as string) || '';
    const base = 68;
    const extra = Math.max(0, name.length - 12) * 3.5;
    return Math.min(base + extra, 170);
  }

  private nodeTextMaxWidth(ele: NodeSingular): string {
    return `${Math.round(this.nodeSize(ele) * 0.85)}px`;
  }

  private colorFor(type: string): { bg: string; border: string } {
    const key = (type || '').toLowerCase();
    if (key === 'file' || key === 'repository') return this.colors.file;
    if (key === 'class' || key === 'interface') return this.colors.class;
    if (key === 'function' || key === 'module') return this.colors.function;
    return this.colors.other;
  }

  private initGraph() {
    const container = this.graphCanvas?.nativeElement;
    if (!container || this.cy) return;

    this.cy = cytoscape({
      container,
      elements: [],
      style: [
        {
          selector: 'node',
          style: {
            'background-color': (ele: NodeSingular) => this.colorFor(ele.data('type')).bg,
            'border-color': (ele: NodeSingular) => this.colorFor(ele.data('type')).border,
            'border-width': 2,
            'label': 'data(name)',
            'font-family': 'Poppins, sans-serif',
            'font-size': '10px',
            'color': '#0F172A',
            'text-valign': 'center',
            'text-halign': 'center',
            'text-wrap': 'wrap',
            'text-max-width': (ele: NodeSingular) => this.nodeTextMaxWidth(ele),
            'width': (ele: NodeSingular) => this.nodeSize(ele),
            'height': (ele: NodeSingular) => this.nodeSize(ele),
            'shape': 'ellipse'
          }
        },
        {
          selector: 'node:selected',
          style: {
            'border-width': 3,
            'border-color': '#6366F1',
            'overlay-opacity': 0.2,
            'overlay-color': '#6366F1'
          }
        },
        {
          selector: 'edge',
          style: {
            'width': 1.5,
            'line-color': this.colors.edge.color,
            'target-arrow-color': this.colors.edge.color,
            'target-arrow-shape': 'triangle',
            'arrow-scale': 0.8,
            'curve-style': 'bezier',
            'label': 'data(relationship)',
            'font-family': 'Poppins, sans-serif',
            'font-size': '9px',
            'color': '#64748B',
            'text-rotation': 'autorotate',
            'text-margin-y': -8
          }
        },
        {
          selector: 'edge:selected',
          style: {
            'width': 2.5,
            'line-color': '#6366F1',
            'target-arrow-color': '#6366F1'
          }
        }
      ],
      layout: { name: 'preset' },
      wheelSensitivity: 0.3,
      minZoom: 0.1,
      maxZoom: 4,
      boxSelectionEnabled: true
    });

    this.cy.on('select', 'node', (evt: EventObject) => {
      const node = evt.target as NodeSingular;
      this.showNodeDetails(node.data() as NodeDataDefinition);
    });

    this.cy.on('select', 'edge', (evt: EventObject) => {
      const edge = evt.target as EdgeSingular;
      this.showEdgeDetails(edge.data() as EdgeDataDefinition);
    });

    this.cy.on('unselect', () => this.clearSelection());
  }

  private showNodeDetails(data: NodeDataDefinition) {
    this.selectedType.set('node');
    this.selectedTitle.set((data['fullName'] as string) || (data['name'] as string) || data.id || '');
    this.selectedSubtitle.set((data['type'] as string) || 'Node');
    this.selectedMeta.set([
      { label: 'ID', value: data.id || '' },
      { label: 'Type', value: (data['type'] as string) || '—' },
      { label: 'Repository', value: (data['repoName'] as string) || '—' }
    ]);
  }

  private showEdgeDetails(data: EdgeDataDefinition) {
    this.selectedType.set('edge');
    this.selectedTitle.set((data['relationship'] as string) || (data['type'] as string) || 'Relationship');
    this.selectedSubtitle.set('Edge');
    this.selectedMeta.set([
      { label: 'ID', value: data.id || '' },
      { label: 'Type', value: (data['type'] as string) || '—' },
      { label: 'Source', value: data.source || '' },
      { label: 'Target', value: data.target || '' }
    ]);
  }
}
